import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/portfolio", label: t.nav.work },
    { to: "/about", label: t.nav.about },
    { to: "/blog", label: t.nav.blog },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="main-nav"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        padding: scrolled ? "14px 0" : "26px 0",
        background: scrolled ? "rgba(10,10,10,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(139,32,53,0.25)" : "1px solid transparent",
        transition: "all 0.5s var(--ease-cinema)",
      }}
    >
      <div className="flex items-center justify-between" style={{ padding: "0 clamp(24px, 6vw, 96px)" }}>
        <Link to="/" data-testid="nav-logo" className="font-display" style={{ fontSize: "1.75rem", fontWeight: 400, letterSpacing: "0.04em", color: "var(--warm-off-white)", textDecoration: "none" }}>
          Vakar<span style={{ color: "var(--gold)" }}>.</span>
        </Link>

        <nav className="hidden lg:flex items-center" style={{ gap: 36 }}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="link-underline font-mono"
              style={({ isActive }) => ({
                fontSize: "0.75rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: isActive ? "var(--gold)" : "var(--warm-off-white)",
                textDecoration: "none",
              })}
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center" style={{ gap: 16 }}>
          <div className="flex items-center font-mono" style={{ fontSize: "0.75rem", letterSpacing: "0.18em", color: "var(--warm-off-white)" }}>
            <button data-testid="lang-tr" onClick={() => setLang("tr")} style={{ background: "transparent", border: "none", color: lang === "tr" ? "var(--gold)" : "rgba(248,244,240,0.55)", padding: "4px 8px" }}>TR</button>
            <span style={{ color: "rgba(248,244,240,0.3)" }}>|</span>
            <button data-testid="lang-en" onClick={() => setLang("en")} style={{ background: "transparent", border: "none", color: lang === "en" ? "var(--gold)" : "rgba(248,244,240,0.55)", padding: "4px 8px" }}>EN</button>
          </div>
          <button data-testid="nav-mobile-toggle" className="lg:hidden" onClick={() => setOpen(true)} style={{ background: "transparent", border: "none", color: "var(--warm-off-white)" }}>
            <Menu size={26} />
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        data-testid="mobile-menu"
        style={{
          position: "fixed", inset: 0, background: "var(--rich-black)", zIndex: 100,
          display: "flex", flexDirection: "column",
          opacity: open ? 1 : 0, visibility: open ? "visible" : "hidden",
          transform: open ? "translateY(0)" : "translateY(-20px)",
          transition: "all 0.5s var(--ease-cinema)",
        }}
      >
        <div className="flex items-center justify-between" style={{ padding: "26px clamp(24px, 6vw, 96px)" }}>
          <span className="font-display" style={{ fontSize: "1.75rem", color: "var(--warm-off-white)" }}>Vakar<span style={{ color: "var(--gold)" }}>.</span></span>
          <button data-testid="mobile-close" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "var(--warm-off-white)" }}>
            <X size={28} />
          </button>
        </div>
        <nav className="flex flex-col items-start" style={{ padding: "32px clamp(24px, 6vw, 96px)", gap: 24 }}>
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              className="font-display"
              style={{
                fontSize: "2.5rem", color: "var(--warm-off-white)", textDecoration: "none",
                opacity: open ? 1 : 0, transform: open ? "translateY(0)" : "translateY(20px)",
                transition: `all 0.6s var(--ease-cinema) ${0.1 + i * 0.06}s`,
              }}
              onClick={() => setOpen(false)}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-6 font-mono" style={{ fontSize: "0.75rem", letterSpacing: "0.18em", color: "var(--warm-off-white)" }}>
            <button onClick={() => setLang("tr")} style={{ background: "transparent", border: "none", color: lang === "tr" ? "var(--gold)" : "rgba(248,244,240,0.55)", padding: "4px 8px" }}>TR</button>
            <span style={{ color: "rgba(248,244,240,0.3)" }}>|</span>
            <button onClick={() => setLang("en")} style={{ background: "transparent", border: "none", color: lang === "en" ? "var(--gold)" : "rgba(248,244,240,0.55)", padding: "4px 8px" }}>EN</button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Nav;
